import { ActivityIndicator, Pressable, ScrollView } from 'react-native';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../../styles/colors';
import DraftBlock from './DraftBlock';

/**
 * MobileDraftSheet — iOS-Safari-safe bottom-sheet that wraps DraftBlock for
 * the mobile (width < 768) layout.
 *
 * Layout notes:
 *   - Uses `position: absolute` (NOT fixed/sticky). iOS Safari collapses its
 *     toolbar when the user scrolls, which causes `position: fixed` and
 *     `position: sticky` elements to jump. Absolute positioning relative to the
 *     scrollable container stays stable.
 *   - REQUIREMENT FOR PARENT: the outer screen container MUST have
 *     `position: relative` (or `position: absolute`) so that this component's
 *     `position: absolute` anchors correctly. The parent must also use
 *     `height: 100dvh` with a `100vh` fallback so the bottom edge lines up with
 *     the visible viewport. This is the orchestrator's responsibility (Task 27b).
 *   - `transform: translateY(calc(100% - 56px))` for collapsed state: the sheet
 *     sits almost entirely off-screen, with only the 56px grip/header row peeking
 *     above the bottom edge. Expanding slides it up to translateY(0).
 *   - Transition: 250ms ease-out on transform.
 *
 * Visibility contract:
 *   The orchestrator (NegotiationChatScreen) decides whether to mount this
 *   component at all, based on `selectActionSurface`. The sheet is only mounted
 *   when `surface ∈ {'draft', 'guardrail', 'needs_input', 'generating'}`. This
 *   component does NOT contain its own hide/show logic — it always renders when
 *   mounted.
 *
 * Props:
 *   All DraftBlock props (activeDrafts, selectedVersionId, onSelectVersion,
 *   onApprove, onEdit, onRegenerate, onDiscard, onReport, onEnd, regenCount, regenMax,
 *   negotiation, surface)
 *   PLUS:
 *   expanded    boolean — true when the sheet is fully open
 *   onExpand    () => void — called when the user taps the grip to open
 *   onCollapse  () => void — called when the user taps the grip to close
 */
const MobileDraftSheet = ({
  // DraftBlock props
  activeDrafts,
  selectedVersionId,
  onSelectVersion,
  onApprove,
  onEdit,
  onSaveEdit,
  onRegenerate,
  onDiscard,
  onReport,
  onEnd,
  regenCount,
  regenMax,
  negotiation,
  surface,
  // Sheet-specific props
  expanded,
  onExpand,
  onCollapse,
}) => {
  const isGenerating = surface === 'generating';

  const handleGripPress = () => {
    if (isGenerating) return;
    if (expanded) {
      onCollapse?.();
    } else {
      onExpand?.();
    }
  };

  const peekLabel = isGenerating
    ? 'Composing your opening draft...'
    : 'Draft ready - tap to view';

  return (
    <SheetContainer>
      {/* Grip + peek header row — always visible */}
      <GripRow
        onPress={handleGripPress}
        disabled={isGenerating}
        accessibilityRole="button"
        accessibilityState={{ disabled: isGenerating, expanded: !!expanded }}
        accessibilityLabel={
          isGenerating
            ? 'Composing draft'
            : expanded
              ? 'Collapse draft sheet'
              : 'Expand draft sheet'
        }
        accessibilityHint={
          isGenerating
            ? 'Draft is being prepared'
            : expanded
              ? 'Tap to hide the draft'
              : 'Tap to view the suggested message'
        }
      >
        <GripPill />
        {!expanded && (
          <PeekRow>
            {isGenerating ? (
              <ActivityIndicator size="small" color={REDESIGN_COLORS.textSecondary} />
            ) : null}
            <PeekLabel>{peekLabel}</PeekLabel>
          </PeekRow>
        )}
      </GripRow>

      {/* Body — only rendered when expanded and not generating */}
      {expanded && !isGenerating ? (
        <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ paddingBottom: 16 }}>
          <DraftBlock
            activeDrafts={activeDrafts}
            selectedVersionId={selectedVersionId}
            onSelectVersion={onSelectVersion}
            onApprove={onApprove}
            onEdit={onEdit}
            onSaveEdit={onSaveEdit}
            onRegenerate={onRegenerate}
            onDiscard={onDiscard}
            onReport={onReport}
            onEnd={onEnd}
            regenCount={regenCount}
            regenMax={regenMax}
            negotiation={negotiation}
            surface={surface}
          />
        </ScrollView>
      ) : null}
    </SheetContainer>
  );
};

// ---------- Styled components ----------

/**
 * SheetContainer:
 *   position: absolute so it anchors to its relatively-positioned parent
 *   (the screen container). bottom/left/right: 0 places it flush with the
 *   bottom of the viewport.
 *
 *   We don't use `transform: translateY(calc(100% - 56px))` — RN-Web's CSS
 *   parser rejects calc() inside transforms. Instead the container grows
 *   naturally: when collapsed only the 56px GripRow renders, so the sheet
 *   is exactly 56px tall and "peeks" above the bottom edge. When expanded,
 *   the body mounts and the container expands to fit. We cap height so the
 *   sheet never covers the whole screen.
 *
 *   z-index: 20 keeps it above chat content but below modals/overlays.
 */
const SheetContainer = styled.View`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 80%;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-top-width: 1px;
  border-left-width: 1px;
  border-right-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  z-index: 20;
`;

/**
 * GripRow:
 *   Height 56px — the amount that peeks above the bottom edge when collapsed.
 *   Tap target covers the full row so it's easy to hit on mobile.
 */
const GripRow = styled(Pressable)`
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  gap: 8px;
`;

/**
 * GripPill:
 *   Standard drag-grip affordance at the top of the sheet.
 *   32px wide × 4px tall, centered horizontally.
 */
const GripPill = styled.View`
  width: 32px;
  height: 4px;
  border-radius: 999px;
  background-color: ${REDESIGN_COLORS.borderSubtle};
`;

const PeekRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const PeekLabel = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 13px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

export default MobileDraftSheet;
